import { PROJECT_STORE_NAME } from '@/constants/store/ProjectStoreConstant';
import { Project } from '@/interfaces/project.interface';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface QueuedUpdate {
  id: string;
  data: Partial<Project>;
  timestamp: number;
  retries: number;
}

interface ProjectStore {
  projects: Record<string, { data: Project; timestamp: number }>;
  offlineQueue: QueuedUpdate[];
  setProject: (id: string, data: Project) => void;
  getProject: (id: string) => Project | null;
  queueUpdate: (id: string, data: Partial<Project>) => void;
  removeQueuedUpdate: (id: string) => void;
  getQueuedUpdates: () => QueuedUpdate[];
  clearOfflineQueue: () => void;
}

export const useProjectStore = create<ProjectStore>()(
  persist(
    (set, get) => ({
      projects: {},
      offlineQueue: [],

      setProject: (id, data) => {
        set((state) => ({
          projects: {
            ...state.projects,
            [id]: {
              data,
              timestamp: Date.now(),
            },
          },
        }));
      },

      getProject: (id) => {
        const entry = get().projects[id];
        if (!entry) return null;

        const stale = Date.now() - entry.timestamp > 5 * 60 * 1000; // 5 minutes
        return stale ? null : entry.data;
      },

      queueUpdate: (id, data) => {
        set((state) => {
          // Remove existing queue entry for this project if any
          const filteredQueue = state.offlineQueue.filter(
            (item) => item.id !== id
          );

          return {
            offlineQueue: [
              ...filteredQueue,
              {
                id,
                data,
                timestamp: Date.now(),
                retries: 0,
              },
            ],
          };
        });
      },

      removeQueuedUpdate: (id) => {
        set((state) => ({
          offlineQueue: state.offlineQueue.filter((item) => item.id !== id),
        }));
      },

      getQueuedUpdates: () => {
        return get().offlineQueue;
      },

      clearOfflineQueue: () => {
        set({ offlineQueue: [] });
      },
    }),

    {
      name: PROJECT_STORE_NAME,
      version: 1,
    }
  )
);
